import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  InputNumber,
  Row,
  Space,
  Spin,
  Tooltip,
} from "antd";
import MainLayout from "../../../../../components/layouts/MainLayout";
import TopContent from "../../../../../components/layouts/TopContent";
import cnt from "../../../../../services/definedConstant";
import { BOM } from "../../../../../services/definedMethod";

import SuccessModal from "../../../component/SuccessModal";
import { apiConfig } from "../../../contractbooking.config";

const AmendBondContract = (props) => {
  const { userData, initialValues = null } = props;
  const { session } = userData;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [templateProdDetail, setTemplateProdDetail] = useState({});

  const [fixedCouponRate, setFixedCouponRate] = useState(0);
  const [tenorDays, setTenorDays] = useState(30);

  // Memoized calculation function
  // const calculateMaturityValue = useCallback(() => {
  //   const amountPlaced = form.getFieldValue("amountPlaced");
  //   const currentFixedCouponRate =
  //     form.getFieldValue("fixedCouponRate") || fixedCouponRate;
  //   const currentTenor = form.getFieldValue("tenor") || tenorDays;
  //   const startDate = form.getFieldValue("startDate");

  //   if (
  //     !amountPlaced ||
  //     !currentFixedCouponRate ||
  //     !startDate ||
  //     !currentTenor
  //   ) {
  //     return;
  //   }

  //   const principal = parseFloat(amountPlaced);
  //   const rate = parseFloat(currentFixedCouponRate) / 100;
  //   const tenorInYears = currentTenor / 365;

  //   let maturityValue = 0;

  //   if (interestType === "SI") {
  //     const interest = principal * rate * tenorInYears;
  //     maturityValue = principal + interest;
  //   } else if (interestType === "CI") {
  //     let n = 1;

  //     switch (compoundingFrequency) {
  //       case "semiannually":
  //         n = 2;
  //         break;
  //       case "quarterly":
  //         n = 4;
  //         break;
  //       case "monthly":
  //         n = 12;
  //         break;
  //       case "daily":
  //         n = 365;
  //         break;
  //       default:
  //         n = 1;
  //     }

  //     maturityValue = principal * Math.pow(1 + rate / n, n * tenorInYears);
  //   }

  //   maturityValue = Math.round(maturityValue * 100) / 100;
  //   form.setFieldsValue({ maturityValue });
  // }, [form, interestRate, tenorDays, interestType, compoundingFrequency]);

  // // Update maturity date when tenor changes
  // const updateMaturityDate = useCallback(() => {
  //   const startDate = form.getFieldValue("startDate");
  //   const currentTenor = form.getFieldValue("tenor") || tenorDays;

  //   if (startDate && currentTenor) {
  //     const maturityDate = dayjs(startDate).add(currentTenor, "day");
  //     form.setFieldsValue({ maturityDate });
  //   }
  // }, [form, tenorDays]);

  // Validator for tenor
  const validateDefaultTenor = (_, value) => {
    const minTenor = templateProdDetail?.tenor;

    if (value < minTenor) {
      return Promise.reject(`Tenor cannot be less than ${minTenor} days`);
    }
    return Promise.resolve();
  };

  // Handle tenor change
  const handleTenorChange = (value) => {
    setTenorDays(value);
  };

  // Handle interest rate change
  const handleFixedCouponRateChange = (value) => {
    setFixedCouponRate(value);
  };

  // Initial API call
  useEffect(() => {
    if (state?.contractObj?.product?.id) {
      getBondProductDetails(state?.contractObj);
    }
  }, [state]);

  const getBondProductDetails = (contractParam) => {
    const url = `${apiConfig.GetBondProduct}/${contractParam?.product?.id}`;
    let body = { session };

    setIsLoading(true);

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        setIsLoading(false);
      } else {
        setTemplateProdDetail(res);
        const objList = res;

        const fixedCouponRate =
          contractParam?.fixedCouponRate ?? objList?.fixedCouponRate;
        const defaultTenor = contractParam?.tenor || objList?.defaultTenor;

        setFixedCouponRate(fixedCouponRate);
        setTenorDays(defaultTenor);

        form.setFieldsValue({
          fixedCouponRate: fixedCouponRate,
          tenor: defaultTenor,
        });

        setIsLoading(false);
      }
    });
  };

  function onCancel() {
    navigate("/detail-bond-contract", { state: state });
  }

  const onSubmit = (formValues) => {
    const payloadData = {};

    payloadData.productId = templateProdDetail?.id;
    payloadData.clientId = state?.customerId;
    payloadData.investerType = state?.contractObj?.investorType?.id;
    payloadData.clientId = state?.contractObj?.client?.id;
    payloadData.dealInitiationDate = BOM.DateTime(
      state?.contractObj?.dealInitiationDate,
      "only-date"
    );
    payloadData.bondType = state?.contractObj?.bondType;
    payloadData.issuer = state?.contractObj?.issuer;
    payloadData.issueDate = BOM.DateTime(
      state?.contractObj?.issueDate,
      "only-date"
    );
    payloadData.amount = state?.contractObj?.amount;
    payloadData.purchasePrice = state?.contractObj?.purchasePrice;
    payloadData.numberOfUnits = state?.contractObj?.numberOfUnits;
    payloadData.totalConsideration = state?.contractObj?.totalConsideration;
    payloadData.settlementDate = BOM.DateTime(
      state?.contractObj?.settlementDate,
      "only-date"
    );
    payloadData.settlementAccountId = state?.contractObj?.settlementAccount?.id;
    payloadData.kycStatus = state?.contractObj?.kycStatus?.id;
    payloadData.amlScreening = state?.contractObj?.amlScreening?.id;
    payloadData.sanctionScreening = state?.contractObj?.sanctionScreening?.id;
    payloadData.centralBankReport = state?.contractObj?.centralBankReport?.id;
    payloadData.issuerRiskRating = 1;
    payloadData.marketRiskConsideration = 1;
    payloadData.concentrationRisk = 1;
    payloadData.applicationFormStatus =
      state?.contractObj?.applicationFormStatus?.value;
    payloadData.bondPurchaseAgreement =
      state?.contractObj?.bondPurchaseAgreement?.value;
    payloadData.identificationDocuments =
      state?.contractObj?.identificationDocuments?.value;
    payloadData.corporateResolution =
      state?.contractObj?.corporateResolution?.value;
    payloadData.taxClearanceCertificate =
      state?.contractObj?.taxClearanceCertificate?.value;
    payloadData.fixedCouponRate = formValues?.fixedCouponRate;
    payloadData.tenor = formValues?.tenor;

    const url = `${apiConfig.EditBondContract}/${state?.id}`;

    const body = {
      // ...formValues,
      ...payloadData,
      dateFormat: "dd MMMM yyyy",
      locale: "en",
      method: "PUT",
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        setIsModalVisible(true);
        setIsLoading(false);
      }
    });
  };

  const handleGoBack = () => {
    navigate("/detail-bond-contract", {
      state: { ...state, statusValue: "approved" },
    });
  };

  // Get min values for validation
  const minTenor = templateProdDetail?.tenor || 1;

  return (
    <MainLayout isEnableRightSidebar={true}>
      <TopContent pageTitle={"Amend Deal"} pageItems={[]} />

      <Spin spinning={isLoading} size="large">
        <Card
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Row>
              <Divider orientation="left">
                {`Contract Ref ID: ${state?.contractRefId}`}
              </Divider>

              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Fixed Coupon Rate
                      <Tooltip>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="fixedCouponRate"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Rate Value",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: "44px" }}
                    placeholder="0.00"
                    suffix="%"
                    min={0}
                    // max={100}
                    // step={0.01}
                    onChange={handleFixedCouponRateChange}
                  />
                </Form.Item>
              </Col>

              {/* Tenor Field - User can modify */}
              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Tenor (Years)
                      <Tooltip>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="tenor"
                  rules={[
                    {
                      required: true,
                      message: "Please enter tenor",
                    },
                    {
                      validator: validateDefaultTenor,
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: "44px" }}
                    placeholder="30"
                    suffix="years"
                    min={minTenor}
                    onChange={handleTenorChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <div style={{ paddingTop: 20, borderRadius: 20 }}>
              <Flex justify="end" gap="10px" align="end">
                <Button
                  style={{
                    backgroundColor: "white",
                    borderRadius: 8,
                    border: "1px solid #D0D5DD",
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: "#15CCAE",
                    color: "white",
                    border: "1px solid #BA24D5",
                    borderRadius: 8,
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                  htmlType="submit"
                >
                  Submit
                </Button>
              </Flex>
            </div>
          </Form>
        </Card>

        <SuccessModal
          visible={isModalVisible}
          onClose={handleGoBack}
          successMessage={"Contract Amended successfully"}
        />
      </Spin>
    </MainLayout>
  );
};

export default AmendBondContract;
